import React, { useState } from 'react';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import { Restaurant } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FoodImageUpload from './FoodImageUpload';
import {
  getUserMenuAnalysis,
  addUserMenuAnalysis,
  deleteUserMenuAnalysis,
  downloadUserMenuAnalysis,
  getUserMenuAnalysisDetails,
} from '../../../utils/api';
import useStore from '../../../Store/Store';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRef } from 'react';
import { Pagination } from '@mui/material';
import MenuAnalysisCard from './MenuAnalysisCard';

const FoodMenuAnalysis = ({ onAnalyzeMenu }) => {
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState([]);
  const [currentItem, setCurrentItem] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const user = useStore((state) => state.user);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const uploadRef = useRef(null);

  const {
    data: menuAnalysisData,
    isLoading,
    error,
    refetch,
  } = useQuery(['getUserMenuAnalysis', user?._id], () => getUserMenuAnalysis({ userId: user?._id }), {
    retry: false,
    cacheTime: 0,
  });

  const analysesArray = menuAnalysisData?.data?.analyses || [];
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnalyses = analysesArray.slice(startIndex, endIndex);

  const handleAddItem = () => {
    if (currentItem.trim()) {
      setMenuItems([...menuItems, { id: Date.now(), name: currentItem.trim(), nutrition: null }]);
      setCurrentItem('');
    }
  };

  const handleRemoveItem = (id) => {
    setMenuItems(menuItems.filter((item) => item.id !== id));
  };

  const handleAnalyzeMenu = async () => {
    if (menuItems.length === 0) return;

    setIsAnalyzing(true);
    try {
      await onAnalyzeMenu(menuItems);
    } catch (error) {
      console.error('Menu analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleUpload = async (file) => {
    if (!user?._id) {
      setUploadError(t('errors.notAuthenticated'));
      return;
    }

    setIsProcessing(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_EXPRESS_URL}/api/cancare/user/${user._id}/menu/signedUrl`,
        {
          type: file.type,
          name: file.name,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: localStorage.getItem('auth'),
            'ngrok-skip-browser-warning': 'true',
          },
        },
      );

      const postURL = data?.data?.postURL;

      if (!postURL) {
        throw new Error(t('errors.uploadUrlFailed'));
      }

      await axios.put(postURL, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      await addUserMenuAnalysis({ userId: user._id, body: { name: file.name, link: postURL.split('?')[0] } });

      if (onAnalyzeMenu) {
        await onAnalyzeMenu(file);
      }

      setUploadSuccess(true);
      if (uploadRef.current) {
        uploadRef.current.clearImage();
      }
      setTimeout(() => setUploadSuccess(false), 3000);
      refetch();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.response?.data?.message || error.message || t('errors.uploadFailed'));
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteMenu = async (analysisId) => {
    await deleteUserMenuAnalysis({ userId: user._id, analysisId });
    refetch();
  };

  const handleDownload = async (analysis) => {
    try {
      const response = await downloadUserMenuAnalysis({ userId: user._id, analysisId: analysis._id });
      if (response?.data) {
        const link = document.createElement('a');
        link.href = response?.data;
        link.download = `${analysis.mealName || 'food-analysis'}-${new Date(analysis.uploadDate).toISOString().split('T')[0]}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('No download link received from API');
      }
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  const handleViewDetails = async (analysis) => {
    const response = await getUserMenuAnalysisDetails({ userId: user._id, analysisId: analysis._id });
    setAnalysisDetails(response.data);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(analysesArray.length / itemsPerPage);

  return (
    <Box>
      <Box
        style={{
          padding: '24px',
          borderRadius: '12px',
          border: '1px solid #e0e0e0',
          background: '#fff',
          marginBottom: '20px',
        }}
      >
        <Typography variant="h6" style={{ marginBottom: '16px', fontWeight: '600', color: '#333' }}>
          {t('foodMenu.title')}
        </Typography>

        <FoodImageUpload
          ref={uploadRef}
          onUpload={handleUpload}
          isUploading={isAnalyzing}
          title={t('foodMenu.upload.title')}
          description={t('foodMenu.upload.description')}
          uploadButtonText={t('foodMenu.upload.buttonText')}
          loadingText={t('foodMenu.upload.loadingText')}
          tips={[
            t('foodMenu.upload.tips.visibility'),
            t('foodMenu.upload.tips.lighting'),
            t('foodMenu.upload.tips.frame'),
            t('foodMenu.upload.tips.quality'),
          ]}
        />

        {menuItems.length > 0 && (
          <Box style={{ marginBottom: '20px' }}>
            <Typography variant="subtitle2" style={{ fontWeight: '600', marginBottom: '12px', color: '#333' }}>
              {t('foodMenu.menuItems', { count: menuItems.length })}
            </Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {menuItems.map((item) => (
                <Chip
                  key={item.id}
                  label={item.name}
                  onDelete={() => handleRemoveItem(item.id)}
                  style={{
                    backgroundColor: '#e8f5e8',
                    color: '#26786A',
                    border: '1px solid #26786A',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {menuItems.length > 0 && (
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleAnalyzeMenu}
              disabled={isAnalyzing}
              startIcon={<Restaurant />}
              style={{
                backgroundColor: '#26786A',
                color: '#fff',
                borderRadius: '25px',
                padding: '12px 32px',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '16px',
              }}
            >
              {isAnalyzing ? t('foodMenu.analyzing') : t('foodMenu.analyzeButton')}
            </Button>
          </Box>
        )}

        {analysesArray?.length > 0 ? (
          <Box>
            <Typography
              variant="h5"
              style={{
                marginBottom: '20px',
                fontWeight: '600',
                color: '#333',
              }}
            >
              {t('foodMenu.historyTitle')}
            </Typography>

            {currentAnalyses.length > 0 && (
              <Box style={{ marginBottom: '32px' }}>
                {currentAnalyses.map((analysis) => (
                  <MenuAnalysisCard
                    key={analysis._id}
                    analysis={analysis}
                    onDelete={handleDeleteMenu}
                    onDownload={handleDownload}
                    onViewDetails={handleViewDetails}
                    analysisDetails={analysisDetails}
                  />
                ))}
              </Box>
            )}

            {totalPages > 1 && (
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '20px',
                  padding: '20px 0',
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#26786A',
                      '&.Mui-selected': {
                        backgroundColor: '#26786A',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#1e5f54',
                        },
                      },
                      '&:hover': {
                        backgroundColor: '#e8f5e8',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            style={{
              textAlign: 'center',
              padding: '40px',
              background: '#f8f9fa',
              borderRadius: '12px',
              marginTop: '20px',
            }}
          >
            <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
              {t('foodMenu.noAnalyses')}
            </Typography>
            <Typography variant="body2" style={{ color: '#999' }}>
              {t('foodMenu.getStarted')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FoodMenuAnalysis;
